<template>
  <div class="base-input">
    <div class="input-container">
      <input @input="onInput"
             v-on="listeners"
             :value="value"
             :autofocus="autofocus"
             :type="inputType"
             :pattern="inputPattern"
             :placeholder="placeholder"
             :class="{ empty, error, 'with-append': hasAppendSlot, readonly, [size]: size }"
             :min="min"
             :readonly="readonly"
             :autocapitalize="autocapitalize"
             :maxlength="maxLength" />
      <div v-if="hasAppendSlot"
           class="append"
           :class="{ [size]: size }" >
        <slot name="append"/>
      </div>
      <label class="top-right-label">{{ label }}</label>
    </div>

    <label v-if="errorMessage" class="error-message">{{ errorMessage }}</label>
  </div>
</template>

<script>

import _ from 'lodash';

export default {
  name: 'BaseInput',
  data() {
    return {
      empty: _.isEmpty(this.value),
    };
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    maxLength: {
      type: Number,
      default: 1000,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    inputType: {
      type: String,
      default: '',
    },
    inputPattern: {
      type: String,
    },
    placeholder: {
      type: String,
      default: '',
    },
    min: {
      type: String,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'normal',
      validator: val => ['normal', 'large'].includes(val),
    },
    autocapitalize: {
      type: String,
      default: 'none',
    },
  },
  computed: {
    listeners() {
      // We get rid
      // eslint-disable-next-line no-unused-vars
      const { input, ...listeners } = this.$listeners;
      return listeners;
    },
    hasAppendSlot() {
      return !!this.$slots.append;
    },
    error() {
      return !!this.errorMessage;
    },
  },
  methods: {
    onInput(event) {
      const newValue = event.target.value;
      this.$emit('input', newValue);
      this.empty = _.isEmpty(newValue);
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../scss/riseup-colors';

  //Remove arrows from input type number
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }

  .base-input {
    display: flex;
    flex-direction: column;
    position: relative;
    margin-bottom: 20px;
    width: 100%;

    .error-message {
      margin-top: 10px;
      font-size: 14px;
      color: $riseup_danger_red;
      right: 0;
    }

    .input-container {
      display: flex;

      input {
        font-family: SimplerPro;
        width: 100%;
        margin-top: 23px;
        padding: 0 16px;
        border: 1px solid $riseup_gray_2;
        border-radius: 6px;
        color: $riseup_black;

        &.normal {
          font-size: 20px;
          line-height: 20px;
          height: 58px;
        }

        &.large {
          font-size: 34px;
          line-height: 34px;
          height: 78px;
        }

        &.readonly {
          background-color: $riseup_gray_disabled;
          color: $riseup_gray_2;
          border-color: $riseup_gray_0;
        }

        &.with-append {
          padding-left: 75px;
        }

        &:focus {
          outline: none;
          border: 1px solid $riseup_blue;
        }

        &::placeholder {
          color: $riseup_gray_0;
        }

        &:not(.empty):not(:focus):not(.readonly) {
          border: 1px solid $riseup_blue;
        }

        &:not(.empty):not(.readonly).error {
          border: 1px solid $riseup_danger_red;
        }

        &:not(.empty):not(:focus) ~ .top-right-label,
        &:focus ~ .top-right-label {
          position: absolute;
          font-size: 14px;
          color: $riseup_blue;
          transition: 300ms ease all;
          right: 0;
        }

        &.readonly:not(.empty) ~ .top-right-label {
          color: $riseup_gray_2;
        }

        &:not(.empty).error ~ .top-right-label {
          color: $riseup_danger_red;
        }

        &.empty:not(:focus) ~ .top-right-label {
          position: absolute;
          right: 16px;
          color: $riseup_gray_2;
          font-size: 20px;
          pointer-events: none;
          transition: 300ms ease all;
          line-height: 30px;
        }

        &.empty.normal:not(:focus) ~ .top-right-label {
          top: 36px;
        }

        &.empty.large:not(:focus) ~ .top-right-label {
          top: 46px;
        }
      }

      .append {
        position: relative;

        span {
          position: absolute;
          top: 46px;
          left: 24px;
        }
      }
    }
  }

</style>
